import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  getBlock,
  updateBlock,
  appendAppointmentToBlock,
  removeAppointmentFromBlock,
} from "../store/thunks/blocksThunk";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AppointmentSummary from "../applicationUi/components/appointments/AppointmentSummary";
import QuickAddAppointmentModal from "./QuickAddAppointmentModal";

export default function ModifyBlockModal({ isOpen, onClose, blockId }) {
  const dispatch = useDispatch();

  const [blockTitle, setBlockTitle] = useState("N/A");
  const [localAppointments, setLocalAppointments] = useState([]);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [lastAppointmentTime, setLastAppointmentTime] = useState(null);
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const block = useSelector((state) => state.blocks.block);
  const loading = useSelector((state) => state.blocks.loading);
  const error = useSelector((state) => state.blocks.error);

  useEffect(() => {
    if (isOpen && blockId) {
      dispatch(getBlock(blockId));
    }
  }, [isOpen, blockId, dispatch]);

  useEffect(() => {
    if (block) {
      setBlockTitle(block.title || "N/A");
      setLocalAppointments(
        Array.isArray(block.appointments) ? [...block.appointments] : []
      );
    }
  }, [block]);

  const handleClose = () => {
    onClose(); // Only closes this modal, not BlockDetailsModal
  };

  if (!isOpen) return null;

  // Handle drag-and-drop reorder
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const updated = Array.from(localAppointments);
    const [removed] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, removed);
    setLocalAppointments(updated);
  };

  // Show the AppointmentSummary modal when row is clicked
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentSummaryOpen(true);
  };

  // Update block with new appointment order

  const handleSaveChanges = async () => {
    const updatedAppointments = localAppointments.map((appt, idx) => ({
      ...appt,
      blockNumber: idx + 1,
    }));
    const updatedIds = updatedAppointments.map((appt) => appt._id);

    try {
      await dispatch(
        updateBlock({
          blockId: block._id,
          updatedData: { title: blockTitle, appointments: updatedIds },
        })
      ).unwrap();

      window.alert("Block updated successfully!");
      onClose();
    } catch (err) {
      console.error("Failed to update block:", err);
      window.alert("Failed to update block. Check console for details.");
    }
  };

  // Show QuickAdd modal to add an appointment at the end
  const handleAddAppointmentAtEnd = () => {
    if (localAppointments.length > 0) {
      const lastOne = localAppointments[localAppointments.length - 1];
      setLastAppointmentTime(new Date(lastOne.end));
    }
    setShowQuickAddModal(true);
  };

  const handleRemoveFromBlock = async (appointmentId) => {
    try {
      await dispatch(
        removeAppointmentFromBlock({ blockId: block._id, appointmentId })
      ).unwrap();

      alert("Appointment removed successfully!");
      dispatch(getBlock(block._id)); // Refresh block
    } catch (error) {
      console.error("Failed to remove appointment:", error);
      alert("Failed to remove appointment.");
    }
  };

  // QuickAdd callback
  const handleQuickAddSubmit = async (appointmentData) => {
    try {
      await dispatch(
        appendAppointmentToBlock({ blockId: block._id, appointmentData })
      ).unwrap();

      window.alert("Appointment added to block successfully!");
      dispatch(getBlock(block._id)); // Refresh the block data
      setShowQuickAddModal(false);
    } catch (error) {
      console.error("Failed to append appointment:", error);
      window.alert("Failed to add appointment to block.");
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long", // Add this to show the day of the week
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-6xl bg-white rounded-sm shadow-md relative mt-8">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors">
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-4 overflow-y-auto max-h-[70vh] flex flex-col">
          {/* Header Section - Title & Subtitle */}
          <div className="sm:flex sm:items-center mb-4">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold text-gray-900">
                Modify Block
              </h1>
              <p className="mt-1 text-xs text-gray-600">
                Drag and drop appointments to reorder them. Rescheduling needs
                to be done manually.
              </p>
            </div>
          </div>

          {loading && <p className="text-xs text-gray-600">Loading block information...</p>}
          {error && (
            <p className="text-xs text-red-600">
              Error:{" "}
              {typeof error === "object"
                ? error.message || JSON.stringify(error)
                : error}
            </p>
          )}

          <div className="flex items-center space-x-3">
            <label
              htmlFor="block-title"
              className="text-xs font-medium text-gray-700">
              Block Title:
            </label>
            <input
              id="block-title"
              type="text"
              value={blockTitle}
              onChange={(e) => setBlockTitle(e.target.value)}
              placeholder="Enter Block Title"
              className="block w-full max-w-md px-2 py-1.5 text-xs text-gray-900 bg-white border border-gray-200 rounded-sm outline-none focus:border-indigo-500 focus:ring-indigo-500/20 shadow-sm"
            />
          </div>

          {/* Table Container */}
          <div className="mt-4 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden ring-1 ring-black/5 sm:rounded-sm">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="appointments">
                      {(provided) => (
                        <table
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="min-w-full divide-y divide-gray-200">
                          {/* Table Head */}
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                Actions
                              </th>
                              <th
                                scope="col"
                                className="py-2 pr-3 pl-4 text-left text-xs font-medium text-gray-500 sm:pl-6">
                                Session
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                Service Date
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                Client
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                Title
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                Status
                              </th>
                            </tr>
                          </thead>

                          {/* Table Body */}
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {localAppointments.map((appt, index) => (
                              <Draggable
                                key={appt._id}
                                draggableId={appt._id}
                                index={index}>
                                {(provided) => (
                                  <tr
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() =>
                                      handleAppointmentClick(appt)
                                    }>
                                    <td className="px-3 py-2 text-xs text-gray-500">
                                      <button
                                        onClick={() =>
                                          handleRemoveFromBlock(appt._id)
                                        }
                                        className="text-red-600 font-medium text-xs hover:text-red-700 transition-colors">
                                        Remove
                                      </button>
                                    </td>

                                    <td className="py-2 pr-3 pl-4 text-xs font-medium whitespace-nowrap text-gray-900 sm:pl-6">
                                      {index + 1}
                                    </td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                                      {formatDateTime(appt.start)}
                                    </td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                                      {appt.client?.firstName}{" "}
                                      {appt.client?.lastName || "Unknown"}
                                    </td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                                      {appt.service?.description} - $
                                      {appt.service?.price || "Unknown"}
                                    </td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                                      {appt.status?.length
                                        ? appt.status.join(", ")
                                        : "No Status"}
                                    </td>
                                  </tr>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </tbody>
                        </table>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="mt-4 flex justify-between space-x-2">
            <div>
              <button
                type="button"
                onClick={handleAddAppointmentAtEnd}
                className="rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 transition-colors shadow-sm">
                Add Appointment at End
              </button>
            </div>
            <div className="flex gap-2">
              <button
                className="rounded-sm bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                onClick={onClose}>
                Close
              </button>
              <button
                className="rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 transition-colors shadow-sm"
                onClick={handleSaveChanges}>
                Update Block
              </button>
            </div>
          </div>
        </div>

        {/* Appointment Summary Modal */}
        {isAppointmentSummaryOpen && (
          <AppointmentSummary
            open={isAppointmentSummaryOpen}
            onClose={() => {
              setIsAppointmentSummaryOpen(false);
              dispatch(getBlock(blockId)); // Re-fetch on close
            }}
            appointment={selectedAppointment}
          />
        )}

        {/* Quick Add Appointment Modal */}
        {showQuickAddModal && (
          <QuickAddAppointmentModal
            isOpen={showQuickAddModal}
            onClose={() => setShowQuickAddModal(false)}
            initialDate={lastAppointmentTime?.toISOString().split("T")[0]}
            initialTime={lastAppointmentTime
              ?.toISOString()
              .split("T")[1]
              ?.slice(0, 5)}
            selectedClinician={block?.clinician}
            clinic={block?.clinic}
            blockId={blockId}
            onSubmit={handleQuickAddSubmit}
          />
        )}
      </div>
    </div>
  );
}
